// RendererPresence.js
// Cross-window registry to track which FileViewerRenderer instances are rendering which fileIds.
// Updates a shared localStorage key and emits a window event so other parts of the app can react.

// Copyright Viktor Eberhardt, 2025
// Author: Viktor Eberhardt

// Framework imports

// External imports

// MUI imports (none)

// HSA imports (none)

const STORAGE_KEY = "hsaRendererPresenceV1";
const LOCAL_EVENT = "hsaRendererPresenceUpdated";
const WINDOW_ID_KEY = "hsaWindowId";
// Heartbeat-based expiry so stale entries from force-closes/crashes are removed automatically
const HEARTBEAT_MS = 3000; // how often this window refreshes its liveness while it has active renders
const EXPIRY_MS = 15000; // consider a window dead if it hasn't heartbeated in this time

function getWindowId() {
  try {
    let id = sessionStorage.getItem(WINDOW_ID_KEY);
    if (!id) {
      id = `w-${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      sessionStorage.setItem(WINDOW_ID_KEY, id);
    }
    return id;
  } catch (e) {
    // Fallback when sessionStorage is not available
    return "w-fallback";
  }
}

function readRegistry() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    const reg = typeof obj === "object" && obj ? obj : {};
    // Prune stale windows (no heartbeat within EXPIRY_MS or missing lastSeen)
    const now = Date.now();
    let changed = false;
    for (const w of Object.keys(reg)) {
      const last = Number(reg[w]?.lastSeen || 0);
      if (!Number.isFinite(last) || now - last > EXPIRY_MS) {
        delete reg[w];
        changed = true;
      }
    }
    if (changed) {
      // Write back the pruned registry to keep storage lean
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(reg));
      } catch (_) {
        // ignore write-back errors
      }
    }
    return reg;
  } catch (e) {
    return {};
  }
}

function writeRegistry(registry) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(registry));
  } catch (e) {
    // ignore write errors
  }
  // Notify same-window listeners
  try {
    const detail = {
      activeIds: Array.from(getActiveIdsFromRegistry(registry)),
    };
    window.dispatchEvent(new CustomEvent(LOCAL_EVENT, { detail }));
  } catch (e) {
    // ignore event errors
  }
}

function getActiveIdsFromRegistry(registry) {
  const active = new Set();
  try {
    for (const winId of Object.keys(registry)) {
      const filesCount = registry[winId] && registry[winId].filesCount;
      if (!filesCount) continue;
      for (const [fid, count] of Object.entries(filesCount)) {
        if (count > 0) active.add(fid);
      }
    }
  } catch (e) {
    // ignore parsing errors
  }
  return active;
}

function cleanupWindowEntry(winId) {
  const reg = readRegistry();
  if (reg[winId]) {
    delete reg[winId];
    writeRegistry(reg);
  }
}

// Ensure we remove this window's entry when it closes
(() => {
  const wid = getWindowId();
  const onUnload = () => {
    try {
      cleanupWindowEntry(wid);
    } catch (e) {
      // ignore
    }
  };
  window.addEventListener("beforeunload", onUnload);
})();

// Internal heartbeat state
let heartbeatTimer = null;
let activeRegistrations = 0;

function startHeartbeatIfNeeded() {
  if (heartbeatTimer || activeRegistrations <= 0) return;
  heartbeatTimer = setInterval(() => {
    try {
      const wid = getWindowId();
      const reg = readRegistry();
      if (!reg[wid]) reg[wid] = { filesCount: {}, lastSeen: Date.now() };
      reg[wid].lastSeen = Date.now();
      writeRegistry(reg);
    } catch (_) {
      // ignore
    }
  }, HEARTBEAT_MS);
}

function stopHeartbeatIfIdle() {
  if (activeRegistrations > 0) return;
  if (heartbeatTimer) {
    try {
      clearInterval(heartbeatTimer);
    } catch (_) {
      // ignore
    }
    heartbeatTimer = null;
  }
}

const RendererPresence = {
  getActiveRenderedIds() {
    return getActiveIdsFromRegistry(readRegistry());
  },
  register(fileId) {
    if (!fileId) return;
    const wid = getWindowId();
    const reg = readRegistry();
    if (!reg[wid]) reg[wid] = { filesCount: {}, lastSeen: Date.now() };
    const filesCount = reg[wid].filesCount || {};
    const cur = Number(filesCount[fileId] || 0);
    filesCount[fileId] = cur + 1;
    reg[wid].filesCount = filesCount;
    reg[wid].lastSeen = Date.now();
    writeRegistry(reg);
    // Track active count and ensure heartbeat is running
    activeRegistrations = Math.max(0, activeRegistrations + 1);
    startHeartbeatIfNeeded();
  },
  unregister(fileId) {
    if (!fileId) return;
    const wid = getWindowId();
    const reg = readRegistry();
    if (!reg[wid] || !reg[wid].filesCount) return;
    const filesCount = reg[wid].filesCount;
    const cur = Number(filesCount[fileId] || 0);
    if (cur <= 1) {
      delete filesCount[fileId];
    } else {
      filesCount[fileId] = cur - 1;
    }
    // Remove empty window entries to keep registry small
    if (Object.keys(filesCount).length === 0) {
      delete reg[wid];
    } else {
      reg[wid].filesCount = filesCount;
      reg[wid].lastSeen = Date.now();
    }
    writeRegistry(reg);
    // Update active count and stop heartbeat if no more registrations
    activeRegistrations = Math.max(0, activeRegistrations - 1);
    stopHeartbeatIfIdle();
  },
  subscribe(callback) {
    if (typeof callback !== "function") return () => {};
    const storageHandler = (e) => {
      if (e && e.key && e.key !== STORAGE_KEY) return;
      try {
        const active = RendererPresence.getActiveRenderedIds();
        callback(active);
      } catch (err) {
        // ignore
      }
    };
    const localHandler = (e) => {
      try {
        const detailActive = new Set(e?.detail?.activeIds || []);
        callback(detailActive);
      } catch (err) {
        // fallback to read directly
        storageHandler({ key: STORAGE_KEY });
      }
    };
    window.addEventListener("storage", storageHandler);
    window.addEventListener(LOCAL_EVENT, localHandler);
    // Initial call
    try {
      callback(RendererPresence.getActiveRenderedIds());
    } catch (e) {
      // ignore initial callback errors
    }

    return () => {
      window.removeEventListener("storage", storageHandler);
      window.removeEventListener(LOCAL_EVENT, localHandler);
    };
  },
  // Optional escape hatch: manually clear the registry (useful for debugging or hard resets)
  resetAll() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      window.dispatchEvent(
        new CustomEvent(LOCAL_EVENT, { detail: { activeIds: [] } })
      );
    } catch (_) {
      // ignore
    }
  },
};

export default RendererPresence;
